import { Trash2Icon, Pencil, X } from 'lucide-react';
import { RenderElementProps, RenderLeafProps } from 'slate-react';
import { memo, useState, useCallback } from 'react';
import { VariableElement } from '../plugins/with-variables';
import { ShortLinkElement } from '../plugins/with-shortlinks';
import { ContactCardElement } from '../plugins/with-contact-cards';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import React from 'react';
import { ReactEditor } from 'slate-react';
import { Editor, Transforms, Path } from 'slate';
import { AnimatedPill } from './animated-components';
import { EditorTooltip, EditorTooltipTrigger, EditorTooltipContent } from './editor-tooltip';
import { useSelected } from 'slate-react';

/**
 * Types for Slate's custom elements. Add new element types here.
 * Note: Keep this in sync with the switch statement in ElementRenderer
 */
export type CustomElement =
  | { type: 'paragraph'; children: CustomText[] }
  | { type: 'image'; url: string; file: File; children: CustomText[] }
  | { type: 'stop-message'; messageText: string; children: CustomText[] }
  | VariableElement
  | ShortLinkElement
  | ContactCardElement;

/**
 * Extends Slate's base text type with our custom decorations
 */
export type CustomText = {
  text: string;
  isSlashCommand?: boolean;
};

/**
 * Props for the ElementRenderer component.
 * Extends Slate's RenderElementProps with our custom properties.
 */
interface ElementRendererProps extends RenderElementProps {
  element: CustomElement;
  editor: Editor;
  onDeleteImage: (element: CustomElement) => void;
  onEditStopMessage?: () => void;
  onEditVariable?: (element: VariableElement) => void;
  onEditShortLink?: (element: ShortLinkElement) => void;
  onEditContactCard?: (element: ContactCardElement) => void;
  onDeleteContactCard?: (id: string) => void;
}

interface InlinePillProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  onEdit?: (e: React.MouseEvent) => void;
  tooltip?: string;
  className?: string;
}

/**
 * Reusable pill component for inline elements (variables, shortlinks)
 * Uses tooltips for edit/delete actions to keep the UI clean
 */
const InlinePill = React.forwardRef<HTMLSpanElement, InlinePillProps>(
  ({ children, onClick, onDelete, onEdit, tooltip, className, ...props }: InlinePillProps, ref) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(e);
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete?.(e);
    };

    const handleEdit = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onEdit?.(e);
    };

    const pill = (
      <span
        ref={ref}
        onClick={handleClick}
        className={cn(
          'inline-flex items-center px-1 py-1 mx-0.5 rounded cursor-pointer transition-colors align-baseline leading-none',
          'bg-secondary hover:bg-secondary/80',
          className
        )}
        {...props}
      >
        {children}
      </span>
    );

    if (!tooltip) {
      return pill;
    }

    return (
      <span>
        <EditorTooltip>
          <EditorTooltipTrigger asChild>{pill}</EditorTooltipTrigger>
          <EditorTooltipContent
            side="top"
            className="flex items-center gap-4 px-3 py-2"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <button
              className="flex items-center gap-2 text-foreground hover:text-foreground/80"
              onClick={handleEdit}
            >
              <Pencil className="h-3 w-3" /> Edit
            </button>
            <Separator orientation="vertical" className="h-4" />
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <X className="h-3 w-3" /> Delete
            </button>
          </EditorTooltipContent>
        </EditorTooltip>
      </span>
    );
  }
);
InlinePill.displayName = 'InlinePill';

interface ContactCardProps {
  element: ContactCardElement;
  children: RenderElementProps['children'];
  attributes?: RenderElementProps['attributes'];
  onEdit?: (element: ContactCardElement) => void;
  onDelete?: (id: string) => void;
}

/**
 * Contact card component for displaying contact information in the editor.
 * Re-render logic is handled by the parent ElementRenderer's memo comparison.
 */
const ContactCard = ({ element, attributes, children, onEdit, onDelete }: ContactCardProps) => {
  const selected = useSelected();

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit?.(element);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(element.data.id);
  };

  return (
    <div {...attributes} className="my-2 inline-block align-bottom" contentEditable={false}>
      <div
        className={cn(
          'relative rounded-lg overflow-hidden group cursor-pointer max-w-[250px]',
          'bg-secondary p-4',
          selected && 'ring-2 ring-primary'
        )}
        onClick={handleEdit}
      >
        <div className="flex items-center gap-3">
          {element.data.image && (
            <img
              src={element.data.image}
              alt=""
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{element.data.name}</div>
            <div className="text-sm text-muted-foreground truncate">
              {element.data.phoneNumbers[0]?.number}
            </div>
          </div>
        </div>

        {/* Overlay with actions */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-black/50 absolute inset-0" />
          <div className="relative flex gap-4">
            <button
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              onClick={handleEdit}
            >
              <Pencil className="h-4 w-4 text-white" />
            </button>
            <button
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              onClick={handleDelete}
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

/**
 * Renders different types of elements in the editor. Some elements are block-level
 * (images, stop messages) while others are inline (variables, shortlinks).
 *
 * Performance: Uses React.memo with custom comparison to prevent unnecessary
 * re-renders, especially important for large documents.
 */
function BaseElementRenderer({
  attributes,
  children,
  element,
  editor,
  onDeleteImage,
  onEditStopMessage,
  onEditVariable,
  onEditShortLink,
  onEditContactCard,
  onDeleteContactCard
}: ElementRendererProps) {
  const [deletingElement, setDeletingElement] = useState<'variable' | 'shortlink' | null>(null);

  const handleInlineDelete = useCallback(
    (type: 'variable' | 'shortlink', path: Path) => {
      setDeletingElement(type);
      setTimeout(() => {
        try {
          Transforms.delete(editor, { at: path });
        } catch (err) {
          console.debug('Delete failed', err);
        }
        setDeletingElement(null);
        ReactEditor.focus(editor);
      }, 150);
    },
    [editor]
  );

  switch (element.type) {
    case 'image':
      return (
        <div {...attributes} className="my-2" contentEditable={false}>
          <div className="relative w-36 h-36 rounded-lg overflow-hidden group inline-block align-bottom">
            <img src={element.url} alt="" className="w-full h-full object-cover" />
            <div
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDeleteImage(element);
              }}
            >
              <div className="bg-black/50 absolute inset-0" />
              <Trash2Icon className="text-white relative" />
            </div>
          </div>
          {children}
        </div>
      );
    case 'stop-message':
      return (
        <div {...attributes} contentEditable={false} className="select-none pointer-events-auto">
          <EditorTooltip>
            <EditorTooltipTrigger asChild>
              <div
                className="inline-flex items-center px-1 py-1 bg-red-50 text-red-700 rounded cursor-pointer hover:bg-red-100 transition-colors leading-none align-baseline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditStopMessage?.();
                }}
              >
                {element.messageText}
              </div>
            </EditorTooltipTrigger>
            <EditorTooltipContent
              side="top"
              className="flex items-center gap-4 px-3 py-2"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <p className="text-sm text-muted-foreground">Required by TCPA</p>
              <Separator orientation="vertical" className="h-4" />
              <button
                className="flex items-center gap-2 text-foreground hover:text-foreground/80"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditStopMessage?.();
                }}
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </EditorTooltipContent>
          </EditorTooltip>
          {children}
        </div>
      );
    case 'variable':
      const variablePath = ReactEditor.findPath(editor, element);
      return (
        <span {...attributes} contentEditable={false} className="inline-flex">
          <AnimatedPill isDeleting={deletingElement === 'variable'}>
            <InlinePill
              onClick={() => onEditVariable?.(element)}
              onEdit={() => onEditVariable?.(element)}
              onDelete={() => {
                handleInlineDelete('variable', variablePath);
              }}
              tooltip="Click to edit"
            >
              {`{{${element.variableName}${element.defaultValue ? ` | default: '${element.defaultValue}'` : ''}}}`}
            </InlinePill>
          </AnimatedPill>
          {children}
        </span>
      );
    case 'shortlink':
      const shortlinkPath = ReactEditor.findPath(editor, element);
      return (
        <span {...attributes} contentEditable={false} className="inline-flex">
          <AnimatedPill isDeleting={deletingElement === 'shortlink'}>
            <InlinePill
              onClick={() => onEditShortLink?.(element)}
              onEdit={() => onEditShortLink?.(element)}
              onDelete={() => {
                handleInlineDelete('shortlink', shortlinkPath);
              }}
              tooltip="Click to edit"
            >
              {element.shortUrl}
            </InlinePill>
          </AnimatedPill>
          {children}
        </span>
      );
    case 'contact-card':
      return (
        <div {...attributes} contentEditable={false}>
          <ContactCard
            element={element}
            attributes={attributes}
            onEdit={onEditContactCard}
            onDelete={onDeleteContactCard}
          >
            {children}
          </ContactCard>
        </div>
      );
    default:
      return (
        <p {...attributes} className="leading-relaxed [&:not(:last-child)]:mb-2">
          {children}
        </p>
      );
  }
}

/**
 * Base LeafRenderer component for rendering text nodes.
 * Handles special formatting for slash commands and regular text.
 *
 * @param props - Component props from Slate's rendering system
 */
function BaseLeafRenderer({ attributes, children, leaf }: RenderLeafProps) {
  if (leaf.isSlashCommand) {
    return (
      <span
        {...attributes}
        className="inline-flex items-center bg-slate-100 text-slate-900 text-sm py-0.5 px-2 mx-0.5 rounded-md"
        contentEditable={false}
      >
        {children}
      </span>
    );
  }
  return <span {...attributes}>{children}</span>;
}

/**
 * Memoize the renderer to prevent unnecessary re-renders.
 * Only re-renders when relevant properties change based on element type.
 */
export const ElementRenderer = memo(BaseElementRenderer, (prev, next) => {
  if (prev.element.type !== next.element.type) return false;

  switch (prev.element.type) {
    case 'image':
      return (
        (prev.element as CustomElement & { url: string }).url ===
        (next.element as CustomElement & { url: string }).url
      );
    case 'stop-message':
      return (
        (prev.element as CustomElement & { messageText: string }).messageText ===
        (next.element as CustomElement & { messageText: string }).messageText
      );
    case 'variable':
      const prevVar = prev.element;
      const nextVar = next.element as VariableElement;
      return (
        prevVar.variableName === nextVar.variableName &&
        prevVar.defaultValue === nextVar.defaultValue &&
        prevVar.category === nextVar.category
      );
    case 'shortlink':
      const prevLink = prev.element;
      const nextLink = next.element as ShortLinkElement;
      return prevLink.shortUrl === nextLink.shortUrl;
    case 'contact-card':
      const prevCard = prev.element;
      const nextCard = next.element as ContactCardElement;

      // Deep comparison of the entire data object
      const prevData = prevCard.data;
      const nextData = nextCard.data;

      return (
        // Compare IDs to ensure we're looking at the same card
        prevData.id === nextData.id &&
        // Compare all relevant data fields
        prevData.name === nextData.name &&
        prevData.image === nextData.image &&
        // Deep compare phone numbers
        JSON.stringify(prevData.phoneNumbers) === JSON.stringify(nextData.phoneNumbers) &&
        // Deep compare emails
        JSON.stringify(prevData.emails) === JSON.stringify(nextData.emails) &&
        // Compare handlers
        prev.onEditContactCard === next.onEditContactCard &&
        prev.onDeleteContactCard === next.onDeleteContactCard
      );
    default:
      return false;
  }
});

/**
 * Handles special formatting for slash commands in the editor
 */
export const LeafRenderer = memo(BaseLeafRenderer, (prev, next) => {
  return prev.leaf.isSlashCommand === next.leaf.isSlashCommand && prev.children === next.children;
});
